import dayjs from 'dayjs';
import { useMemo } from 'react';
import { MapSourceInfo, MapSourceUrlReplacementInfo } from '../../types/other';
import { MapUpdateMode } from '../../types/settings';
import { BaseMapModel, NavMapModel } from '../state/slices/global-slice';
import { useAppSelector } from './store-hooks';

export function useMapTokens() {
    const mapSettings = useMapSourceSettings();
    return useMemo(() => buildUrls(mapSettings.host, mapSettings.base, mapSettings.nav), [mapSettings]);
}

export function useMapSourceSettings() {
    const mode = useAppSelector((x) => x.settings.navMapUpdateMode);
    const settings = useAppSelector((x) => x.global.mapSettings);
    const manualSelected = useAppSelector((x) => x.settings.navMap);

    return useMemo(() => {
        // Automatic means we grab whatever is the latest
        if (mode === MapUpdateMode.automatic) {
            return {
                host: settings.tileHost,
                base: settings.baseSources[0],
                nav: settings.navSources[0],
                updateAvailable: false
            };
        }

        // Use latest base map because I don't see a way to choose base map version
        // Use manually selected map nav version or latest if there wasn't one
        let nav: NavMapModel | undefined = undefined;

        if (manualSelected) {
            nav = {
                company: manualSelected.company,
                sequence: manualSelected.sequence,
                version: manualSelected.version,
                cycleDate: dayjs(manualSelected.cycleDate, { utc: true }).unix(),
                buildDate: dayjs(manualSelected.buildDate, { utc: true }).unix()
            } satisfies NavMapModel;

            // Check if that manually selected version is still available
            if (!settings.navSources.some((x) => areEqual(x, nav!))) {
                // If not, force latest
                nav = settings.navSources[0];
            }
        } else {
            // latest
            nav = settings.navSources[0];
        }

        return {
            host: settings.tileHost,
            // base is always set to latest
            base: settings.baseSources[0],
            nav,
            updateAvailable: !areEqual(nav, settings.navSources[0])
        };
    }, [mode, settings, manualSelected]);
}

function buildUrls(host: string, base: BaseMapModel, nav: NavMapModel): MapSourceUrlReplacementInfo {
    return {
        host,
        base: baseUrl(base),
        nav: navUrl(nav)
    };
}

function baseUrl(model: BaseMapModel | undefined): MapSourceInfo | null {
    if (!model) {
        return null;
    }

    return {
        file: `FEBase-${model.version % 10}-${dayjs.unix(model.buildDate).utc().format('YYYYMMDD')}`,
        encoding: model.version > 1 ? 'mlt' : 'mvt',
        extension: model.version > 1 ? 'mlt' : 'mvt'
    };
}

function navUrl(model: NavMapModel | undefined): MapSourceInfo | null {
    if (!model) {
        return null;
    }

    const cycleDate = dayjs.unix(model.cycleDate).utc();
    const buildDate = dayjs.unix(model.buildDate).utc();

    return {
        file: `FEStatic-${model?.company?.slice(0, 3)}${model.sequence % 10}${model.version % 10}-${cycleDate.format('YYYYMMDD')}-${buildDate.format('YYYYMMDDHHmmss')}`,
        encoding: model.version > 1 ? 'mlt' : 'mvt',
        extension: model.version > 1 ? 'mlt' : 'mvt'
    };
}

function areEqual(a: NavMapModel, b: NavMapModel): boolean {
    return (
        a.buildDate === b.buildDate &&
        a.company === b.company &&
        a.cycleDate === b.cycleDate &&
        a.sequence === b.sequence &&
        a.version === b.version
    );
}
